import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogActions, IconButton, Typography } from '@mui/material';
import type { FC, PropsWithChildren } from 'react';
import { Button } from '~/components/atoms/Button';
import { ImageSanity } from '~/components/atoms/ImageSanity';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import { buildSanityLinkUrl } from '~/utils/link';
import type { ModalProps } from './Modal.types';

/**
 * Displays a dialog with a header image, body content, and action buttons.
 *
 * @param alignment - The alignment of the header image and body content.
 * @param headerImage - The image to display in the header.
 * @param shade - If true, the header and body content will have a shaded
 * background and padding.
 */
export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  alignment = 'left',
  children,
  closeButtonId = 'modal-close-button',
  headerImage,
  headerTitle,
  link,
  maxWidth,
  onClose,
  open,
  shade,
  testId,
}) => {
  const getCompoundTestId = getCompoundTestIdGenerator('modal', testId);

  return (
    <Dialog data-testid={getCompoundTestId()} fullWidth={!!maxWidth} maxWidth={maxWidth} onClose={onClose} open={open}>
      <IconButton
        aria-label="Close dialog"
        data-sp-button-label="Close dialog"
        id={closeButtonId}
        onClick={onClose}
        sx={({ spacing, palette }) => ({
          position: 'absolute',
          right: spacing(),
          top: spacing(),
          color: palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>

      <Box
        alignItems="center"
        bgcolor={({ palette }) => (shade ? palette.grey['300'] : 'transparent')}
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 4 }}
        justifyContent={alignment}
        p={4}
        pb={shade ? 8 : 2}
      >
        {headerImage && (
          <ImageSanity
            imageObject={headerImage}
            width={160}
            height={80}
            styles={{ display: 'block', width: 160 }}
            data-testid={getCompoundTestId('headerImage')}
          />
        )}
        {headerTitle && (
          <Typography data-testid={getCompoundTestId('headerTitle')} variant="h5">
            {headerTitle}
          </Typography>
        )}
      </Box>

      <Box px={4} pb={4}>
        <Box
          bgcolor={({ palette }) => (shade ? palette.grey['100'] : 'transparent')}
          borderRadius={({ shape }) => (shade ? shape.borderRadius : 0)}
          mt={shade ? -4 : 0}
          p={shade ? 4 : 0}
          textAlign={alignment}
        >
          {children}
        </Box>
      </Box>

      {!!link && (
        <DialogActions
          data-testid={getCompoundTestId('actions')}
          sx={{ overflow: 'hidden', p: 4, pt: 0, justifyContent: alignment }}
        >
          <Button
            color="primary"
            id="modal-cta-button"
            onClick={onClose}
            to={buildSanityLinkUrl(link)}
            variant="contained"
          >
            {link.label}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
